import { Minus, Plus, Save, X } from "lucide-react";
import { useState } from "react";

interface ScreeningCriteriaEditorProps {
  initialMustHave: string[];
  initialNiceToHave: string[];
  initialExclusions: string[];
  onSave: (
    mustHave: string[],
    niceToHave: string[],
    exclusions: string[],
    changeSummary: string | undefined,
  ) => void;
  onCancel: () => void;
  saving: boolean;
  requireChangeSummary?: boolean;
}

export function ScreeningCriteriaEditor({
  initialMustHave,
  initialNiceToHave,
  initialExclusions,
  onSave,
  onCancel,
  saving,
  requireChangeSummary = false,
}: ScreeningCriteriaEditorProps) {
  const [mustHave, setMustHave] = useState<string[]>(
    initialMustHave.length > 0 ? initialMustHave : [""],
  );
  const [niceToHave, setNiceToHave] = useState<string[]>(
    initialNiceToHave.length > 0 ? initialNiceToHave : [""],
  );
  const [exclusions, setExclusions] = useState<string[]>(
    initialExclusions.length > 0 ? initialExclusions : [""],
  );
  const [changeSummary, setChangeSummary] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(
      mustHave.filter((s) => s.trim() !== ""),
      niceToHave.filter((s) => s.trim() !== ""),
      exclusions.filter((s) => s.trim() !== ""),
      changeSummary.trim() || undefined,
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Edit Screening Criteria</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving || (requireChangeSummary && !changeSummary.trim())}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <ListField
        label="Must have"
        placeholder="e.g. 5+ years building distributed systems"
        items={mustHave}
        onChange={setMustHave}
      />
      <ListField
        label="Nice to have"
        placeholder="e.g. Experience with Postgres at scale"
        items={niceToHave}
        onChange={setNiceToHave}
      />
      <ListField
        label="Exclusions"
        placeholder="e.g. Currently employed by a direct competitor"
        items={exclusions}
        onChange={setExclusions}
      />

      <div className="flex items-center gap-2">
        <label
          htmlFor="screeningChangeSummary"
          className="text-xs font-medium text-text-secondary whitespace-nowrap"
        >
          Change summary
        </label>
        <input
          id="screeningChangeSummary"
          type="text"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          placeholder={
            requireChangeSummary ? "What changed? (required)" : "What changed? (optional)"
          }
          className="flex-1 rounded border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>
    </form>
  );
}

interface ListFieldProps {
  label: string;
  placeholder: string;
  items: string[];
  onChange: (items: string[]) => void;
}

function ListField({ label, placeholder, items, onChange }: ListFieldProps) {
  const add = () => onChange([...items, ""]);
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const update = (i: number, v: string) => onChange(items.map((x, j) => (j === i ? v : x)));

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="block text-xs font-medium text-text-secondary">{label}</span>
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
        >
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="mt-1 space-y-2">
        {items.map((item, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: dynamic list without stable IDs
          <div key={`${label}-${i}`} className="flex items-center gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 rounded border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder={placeholder}
            />
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
              >
                <Minus size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
